import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import * as yup from 'yup'

import { Socials } from '../../../../common/models'
import FormGroup from '../../../../core/components/FormGroup'
import {
  getSettingsSocials,
  updateSettingsSocials,
} from '../../../../core/services/festival-settings'
import { MessageBox } from '../../messages/MessageBox'
import { Button, Card, Checkbox, Input } from '@/components/ui'

interface ISocialLinksValues {
  instagramEnabled: boolean
  instagramUrl?: string
  youtubeEnabled: boolean
  youtubeUrl?: string
  facebookEnabled: boolean
  facebookUrl?: string
  tiktokEnabled: boolean
  tiktokUrl?: string
  twitterEnabled: boolean
  twitterUrl?: string
  telegramEnabled: boolean
  telegramUrl?: string
  whatsappEnabled: boolean
  whatsappUrl?: string
}

export const SocialLinks: React.FC<{ hasEditPermission: boolean }> = ({
  hasEditPermission,
}) => {
  const { editionId, festivalName } = useParams()

  const [socials, setSocials] = useState<Socials>()
  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  useEffect(() => {
    // prettier-ignore
    (async () => {
      try {
        const resp = await getSettingsSocials()
        if (resp.status === 200) {
          setSocials(resp.data)
          setValue('instagramEnabled', resp.data.instagramEnabled)
          setValue('instagramUrl', resp.data.instagramUrl || '')
          setValue('youtubeEnabled', resp.data.youtubeEnabled)
          setValue('youtubeUrl', resp.data.youtubeUrl || "")
          setValue('facebookEnabled', resp.data.facebookEnabled)
          setValue('facebookUrl', resp.data.facebookUrl || "")
          setValue('twitterEnabled', resp.data.twitterEnabled)
          setValue('twitterUrl', resp.data.twitterUrl || "")
          setValue('telegramEnabled', resp.data.telegramEnabled)
          setValue('telegramUrl', resp.data.telegramUrl || "")
          setValue('whatsappEnabled', resp.data.whatsappEnabled)
          setValue('whatsappUrl', resp.data.whatsappUrl || "")
          setValue('tiktokEnabled', resp.data.tiktokEnabled)
          setValue('tiktokUrl', resp.data.tiktokUrl || "")
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      }
    })()
  }, [editionId, festivalName])

  const schema = yup.object().shape({
    instagramEnabled: yup.boolean().required(),
    instagramUrl: yup.string().when('instagramEnabled', {
      is: (val: boolean) => (val ? true : false),
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.optional(),
    }),
    youtubeEnabled: yup.boolean().required(),
    youtubeUrl: yup.string().when('youtubeEnabled', {
      is: (val: boolean) => (val ? true : false),
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.optional(),
    }),
    facebookEnabled: yup.boolean().required(),
    facebookUrl: yup.string().when('facebookEnabled', {
      is: (val: boolean) => (val ? true : false),
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.optional(),
    }),
    tiktokEnabled: yup.boolean().required(),
    tiktokUrl: yup.string().when('tiktokEnabled', {
      is: (val: boolean) => (val ? true : false),
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.optional(),
    }),
    twitterEnabled: yup.boolean().required(),
    twitterUrl: yup.string().when('twitterEnabled', {
      is: (val: boolean) => (val ? true : false),
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.optional(),
    }),
    telegramEnabled: yup.boolean().required(),
    telegramUrl: yup.string().when('telegramEnabled', {
      is: (val: boolean) => (val ? true : false),
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.optional(),
    }),
    whatsappEnabled: yup.boolean().required(),
    whatsappUrl: yup.string().when('whatsappEnabled', {
      is: (val: boolean) => (val ? true : false),
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.optional(),
    }),
  })

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
    setValue,
    watch,
    control,
  } = useForm<ISocialLinksValues>({
    mode: 'onChange',
    resolver: yupResolver<ISocialLinksValues>(schema),
    defaultValues: {},
  })

  const facebook = watch('facebookEnabled')
  const instagram = watch('instagramEnabled')
  const twitter = watch('twitterEnabled')
  const youtube = watch('youtubeEnabled')
  const telegram = watch('telegramEnabled')
  const whatsapp = watch('whatsappEnabled')
  const tiktok = watch('tiktokEnabled')

  const submit = async (values: ISocialLinksValues) => {
    try {
      const { status } = await updateSettingsSocials(values)
      if (status === 200) {
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Update Successful')
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    }
  }

  const handleCancel = async () => {
    if (socials) {
      setValue('instagramEnabled', socials.instagramEnabled)
      setValue('instagramUrl', socials.instagramUrl)
      setValue('youtubeEnabled', socials.youtubeEnabled)
      setValue('youtubeUrl', socials.youtubeUrl)
      setValue('facebookEnabled', socials.facebookEnabled)
      setValue('facebookUrl', socials.facebookUrl)
      setValue('twitterEnabled', socials.twitterEnabled)
      setValue('twitterUrl', socials.twitterUrl)
      setValue('telegramEnabled', socials.telegramEnabled)
      setValue('telegramUrl', socials.telegramUrl)
      setValue('whatsappEnabled', socials.whatsappEnabled)
      setValue('whatsappUrl', socials.whatsappUrl)
      setValue('tiktokEnabled', socials.tiktokEnabled)
      setValue('tiktokUrl', socials.tiktokUrl)
    }
  }

  return (
    <>
      <Card className="p-7.5 bg-gradient-4 border border-monochrome-120">
        <form
          id="settings-social-links-form"
          className="space-y-7"
          onSubmit={handleSubmit(submit)}
        >
          <h3 className="text-title-xs text-monochrome-1 mb-4">Social Links</h3>
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-monochrome-3-60 border border-monochrome-120 rounded-2xl p-4 space-y-4">
              <FormGroup
                label="instagram"
                direction="row"
                labelClassName="whitespace-nowrap"
                inputWrapperClassName="w-auto"
                wrapperClassName="w-auto flex-row-reverse justify-end"
                labelProps={{
                  htmlFor: 'instagramEnabled',
                }}
              >
                <Controller
                  name="instagramEnabled"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      id="instagramEnabled"
                      ref={field.ref}
                      checked={Boolean(field.value)}
                      onCheckedChange={(checked) =>
                        field.onChange(Boolean(checked))
                      }
                      disabled={!hasEditPermission}
                    />
                  )}
                />
              </FormGroup>
              {instagram && (
                <FormGroup label="instagram Link">
                  <Input
                    isInvalid={!!errors?.instagramUrl?.message}
                    {...register('instagramUrl')}
                    type="text"
                    placeholder="Enter instagram Link"
                    disabled={!hasEditPermission}
                  />
                </FormGroup>
              )}
            </div>
            <div className="bg-monochrome-3-60 border border-monochrome-120 rounded-2xl p-4 space-y-4">
              <FormGroup
                label="youtube"
                direction="row"
                labelClassName="whitespace-nowrap"
                inputWrapperClassName="w-auto"
                wrapperClassName="w-auto flex-row-reverse justify-end"
                labelProps={{
                  htmlFor: 'youtubeEnabled',
                }}
              >
                <Controller
                  name="youtubeEnabled"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      id="youtubeEnabled"
                      ref={field.ref}
                      checked={Boolean(field.value)}
                      onCheckedChange={(checked) =>
                        field.onChange(Boolean(checked))
                      }
                      disabled={!hasEditPermission}
                    />
                  )}
                />
              </FormGroup>
              {youtube && (
                <FormGroup label="youtube Link">
                  <Input
                    isInvalid={!!errors?.youtubeUrl?.message}
                    {...register('youtubeUrl')}
                    type="text"
                    placeholder="Enter youtube Link"
                    disabled={!hasEditPermission}
                  />
                </FormGroup>
              )}
            </div>
            <div className="bg-monochrome-3-60 border border-monochrome-120 rounded-2xl p-4 space-y-4">
              <FormGroup
                label="Facebook"
                direction="row"
                labelClassName="whitespace-nowrap"
                inputWrapperClassName="w-auto"
                wrapperClassName="w-auto flex-row-reverse justify-end"
                labelProps={{
                  htmlFor: 'facebookEnabled',
                }}
              >
                <Controller
                  name="facebookEnabled"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      id="facebookEnabled"
                      ref={field.ref}
                      checked={Boolean(field.value)}
                      onCheckedChange={(checked) =>
                        field.onChange(Boolean(checked))
                      }
                      disabled={!hasEditPermission}
                    />
                  )}
                />
              </FormGroup>
              {facebook && (
                <FormGroup label="Facebook Link">
                  <Input
                    isInvalid={!!errors?.facebookUrl?.message}
                    {...register('facebookUrl')}
                    type="text"
                    placeholder="Enter Facebook Link"
                    disabled={!hasEditPermission}
                  />
                </FormGroup>
              )}
            </div>
            <div className="bg-monochrome-3-60 border border-monochrome-120 rounded-2xl p-4 space-y-4">
              <FormGroup
                label="tiktok"
                direction="row"
                labelClassName="whitespace-nowrap"
                inputWrapperClassName="w-auto"
                wrapperClassName="w-auto flex-row-reverse justify-end"
                labelProps={{
                  htmlFor: 'tiktokEnabled',
                }}
              >
                <Controller
                  name="tiktokEnabled"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      id="tiktokEnabled"
                      ref={field.ref}
                      checked={Boolean(field.value)}
                      onCheckedChange={(checked) =>
                        field.onChange(Boolean(checked))
                      }
                      disabled={!hasEditPermission}
                    />
                  )}
                />
              </FormGroup>
              {tiktok && (
                <FormGroup label="tiktok Link">
                  <Input
                    isInvalid={!!errors?.tiktokUrl?.message}
                    {...register('tiktokUrl')}
                    type="text"
                    placeholder="Enter tiktok Link"
                    disabled={!hasEditPermission}
                  />
                </FormGroup>
              )}
            </div>
            <div className="bg-monochrome-3-60 border border-monochrome-120 rounded-2xl p-4 space-y-4">
              <FormGroup
                label="twitter"
                direction="row"
                labelClassName="whitespace-nowrap"
                inputWrapperClassName="w-auto"
                wrapperClassName="w-auto flex-row-reverse justify-end"
                labelProps={{
                  htmlFor: 'twitterEnabled',
                }}
              >
                <Controller
                  name="twitterEnabled"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      id="twitterEnabled"
                      ref={field.ref}
                      checked={Boolean(field.value)}
                      onCheckedChange={(checked) =>
                        field.onChange(Boolean(checked))
                      }
                      disabled={!hasEditPermission}
                    />
                  )}
                />
              </FormGroup>
              {twitter && (
                <FormGroup label="twitter Link">
                  <Input
                    isInvalid={!!errors?.twitterUrl?.message}
                    {...register('twitterUrl')}
                    type="text"
                    placeholder="Enter twitter Link"
                    disabled={!hasEditPermission}
                  />
                </FormGroup>
              )}
            </div>
            <div className="bg-monochrome-3-60 border border-monochrome-120 rounded-2xl p-4 space-y-4">
              <FormGroup
                label="telegram"
                direction="row"
                labelClassName="whitespace-nowrap"
                inputWrapperClassName="w-auto"
                wrapperClassName="w-auto flex-row-reverse justify-end"
                labelProps={{
                  htmlFor: 'telegramEnabled',
                }}
              >
                <Controller
                  name="telegramEnabled"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      id="telegramEnabled"
                      ref={field.ref}
                      checked={Boolean(field.value)}
                      onCheckedChange={(checked) =>
                        field.onChange(Boolean(checked))
                      }
                      disabled={!hasEditPermission}
                    />
                  )}
                />
              </FormGroup>
              {telegram && (
                <FormGroup label="telegram Link">
                  <Input
                    isInvalid={!!errors?.telegramUrl?.message}
                    {...register('telegramUrl')}
                    type="text"
                    placeholder="Enter telegram Link"
                    disabled={!hasEditPermission}
                  />
                </FormGroup>
              )}
            </div>
            <div className="bg-monochrome-3-60 border border-monochrome-120 rounded-2xl p-4 space-y-4">
              <FormGroup
                label="whatsapp"
                direction="row"
                labelClassName="whitespace-nowrap"
                inputWrapperClassName="w-auto"
                wrapperClassName="w-auto flex-row-reverse justify-end"
                labelProps={{
                  htmlFor: 'whatsappEnabled',
                }}
              >
                <Controller
                  name="whatsappEnabled"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      id="whatsappEnabled"
                      ref={field.ref}
                      checked={Boolean(field.value)}
                      onCheckedChange={(checked) =>
                        field.onChange(Boolean(checked))
                      }
                      disabled={!hasEditPermission}
                    />
                  )}
                />
              </FormGroup>
              {whatsapp && (
                <FormGroup label="whatsapp Link">
                  <Input
                    isInvalid={!!errors?.whatsappUrl?.message}
                    {...register('whatsappUrl')}
                    type="text"
                    placeholder="Enter whatsapp Link"
                    disabled={!hasEditPermission}
                  />
                </FormGroup>
              )}
            </div>
          </div>
        </form>

        <div className="flex justify-end gap-2 pt-4 mt-4 border-t border-monochrome-120 [&>*]:w-[154px]">
          <Button
            onClick={handleCancel}
            variant="secondary"
            disabled={!hasEditPermission}
          >
            Cancel
          </Button>
          <Button
            form="settings-social-links-form"
            type="submit"
            variant="primary"
            disabled={isSubmitting || !hasEditPermission}
            loading={isSubmitting}
          >
            Save
          </Button>
        </div>
      </Card>
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
